
import React from "react";
import ReactDOM from "react-dom/client";
import Snapper from "../../../../shared/Snapper";
import { i18nManager } from "../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Elements } from "../../../../shared/Elements";
import { Schemes, Utils } from "../../../../../utils";
import { AlertInterface, BoolHelper, ButtonManageRef, Checkbox, Classname, Column, DOMHelper, Dropdown, FormControl, NoseurObject, ObjectHelper, RadioButton, Scheme, StringHelper, Table, TableSelectionMode, TextAreaInput, TextInput, useScreenDimension } from "@rivtn/noseur";
import { ApiResponseData, EarningService, ITournamentEditionRegisteredParticipant, ITournamentEdition, ITransaction, RegisteredTournamentStatus, TournamentService, TransactionType, RegisteredTournamentParticipantType, TeamService, SanitizedResponse, TransactionStatus, BaseService, ITournamentEditionCustomField, ITournamentCurrencyBalance } from "../../../../../services";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import { profile } from "console";
import { buildJoinTournamentHeader } from "../../../tournaments/RegisterForTournament";

interface ParticipantsProps {
    historic?: boolean;
    showRecent?: boolean;
    snapTournament?: () => void;
    tournamentEdition: ITournamentEdition;
    dataComponentState?: [React.ReactElement | undefined, React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>];
}

function Participants(props: ParticipantsProps) {

    const { width } = useScreenDimension();
    const { tournamentEdition, showRecent, historic } = props;
    const tournamentService = TournamentService.getInstance();
    const activeDialogMutableRoot = React.useRef<ReactDOM.Root>(null);
    const labels = i18nManager.Labels.host.manage_tournament.participants;
    const [dataComponent, setDataComponent] = props.dataComponentState ?? [];
    const [customFields, setCustomFields] = React.useState<NoseurObject<string>>({});
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [tournamentCurrencies, setTournamentCurrencies] = React.useState<ITournamentCurrencyBalance[]>([]);
    const [tournamentParticipants, setTournamentParticipants] = React.useState<ApiResponseData<ITournamentEditionRegisteredParticipant> | undefined>({});

    React.useEffect(() => {
        tournamentService.queryCurrencies(tournamentEdition.tournament.external_id, {}).then(({ content }: SanitizedResponse<ITournamentCurrencyBalance>) => {
            setTournamentCurrencies(content!);
        }).catch(BaseService.reportError);
        tournamentService.getTournamentEditionCustomFields(tournamentEdition.external_id!).then(({ content }: SanitizedResponse<ITournamentEditionCustomField>) => {
            setCustomFields(content.reduce((acc, t) => {
                acc[t.external_id] = t.label;
                return acc;
            }, {} as NoseurObject<string>));
        }).catch(BaseService.reportError);
        fetchInformation();

        return () => {
            setDataComponent?.(undefined);
        };
    }, []);

    return (<div className="mt-participants" style={showRecent ? { padding: 0 } : undefined}>
        {(!showRecent) && (<>
            {(!historic) && (<div className="mt-participants-navbar">
                <div>
                    {(tournamentParticipants?.content && !tournamentParticipants.content?.length) && (<span className="link danger">{labels.no_participants}</span>)}
                </div>
                <div>
                    <span className="link" onClick={seedParticipants}>{labels.seed_participants}</span>
                    <span className="link" onClick={importParticipants}>{labels.import_participants}</span>
                    <span className="link" onClick={exportParticipants}>{labels.export}</span>
                    <span className="link" onClick={sendInvites}>{labels.invite_participants}</span>
                </div>
            </div>)}
            <div className="mt-query-section">
                {(historic) && (<div className="l" style={{ display: "flex", alignItems: "center", marginTop: 15 }}>
                    <i className="fa fa-arrow-left" style={{ cursor: "pointer" }} onClick={() => window.history.back()} />
                    <span className="lined-limit" style={{ fontSize: 16, marginLeft: 10 }}>{labels.historical_participants}</span>
                </div>)}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 0, marginTop: 10, flex: 1, justifyContent: (historic ? "flex-end" : "flex-start") }}>
                    {historic && (<>
                        <span className="link" style={{ marginRight: 20, marginTop: 10 }} onClick={exportParticipants}>{labels.export}</span>
                        <Snapper header={labels.overview_snap} style={{ marginRight: 20, marginTop: 10 }} template={[{ p: "tour_name", v: tournamentEdition.name }]} />
                    </>)}
                    {Elements.buildConfirmDialogForm(labels.query_inputs.filter((q) => (width > 800 || q.name === "status")), (name, value) => {
                        Utils.updateSearchParams(name, value, setSearchParams, fetchInformation);
                    }, {
                        status: searchParams.get("status"),
                        search: searchParams.get("search"),
                    }, undefined, { flex: 0.5, marginTop: 0, flexDirection: "row", alignItems: "center" }, undefined, {
                        marginRight: 15, marginTop: 0
                    })}
                </div>
                {(!historic) && (<Snapper header={labels.overview_snap} template={[{ p: "tour_name", v: tournamentEdition.name }]} />)}
            </div>
        </>)}
        <div className="mt-table-compound">
            <Table stripedRows scheme={Schemes.OMNEHOST} data={tournamentParticipants?.content} totalRecords={tournamentParticipants?.total_elements} dataRefreshKeys={["external_id", "created_at", "status"]}
                style={{ flex: 1, width: "100%" }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE}
                emptyState={Elements.buildEmptyListView("fa fa-users", (<span>
                    {labels.table.empty_label_parts.have_no}{" "}
                    <span className="link" onClick={sendInvites}>{labels.table.empty_label_parts.send_invite}</span>{" "}
                    {labels.table.empty_label_parts.or}{" "}
                    <span className="link" onClick={() => {
                        DOMHelper.copyToClipboard(Utils.buildPublicUrl("TOURNAMENT", tournamentEdition.tournament.public_id!), () => {
                            Siren.alert(i18nManager.Labels.common.copied, { lifetime: 1000 });
                        });
                    }}>{labels.table.empty_label_parts.copy_link}</span>{" "}
                    {labels.table.empty_label_parts.or}{" "}
                    <span className="link" onClick={props.snapTournament}>{labels.table.empty_label_parts.create_media}</span>{" "}
                </span>), { marginTop: 150 })} selectionMode={TableSelectionMode.SINGLE} rowSelection={true} valuedRowProps={(d) => {
                    return {
                        className: Classname.build("host-tour-row", { "active": (dataComponent as any)?.key === d?.external_id })
                    };
                }} onRowSelection={(tournamentParticipant) => {
                    setDataComponent?.(buildRegisteredTournamentDetails(tournamentParticipant));
                    return false;
                }}>
                <Column className="table-c" doNotRender={width < 800 && !!dataComponent} header={labels.table.headers.registration_date} dataKey="{tournament_edition_registered.registration_date}" template={(v) => Utils.formatToFineDate(v, true)} />
                <Column className="table-c" header={labels.table.headers.name} dataKey="resource" template={(v) => {
                    const name = Utils.getResourceName(v);

                    return (<div style={{ display: "flex", alignItems: "center" }}>
                        <img alt={name} src={Utils.getResourceImage(v)} style={{ width: 20, height: 20, borderRadius: "100%", marginRight: 5 }} />
                        <span>{name}</span>
                    </div>);
                }} />
                <Column className="table-c" doNotRender={!!dataComponent} header={labels.table.headers.registration_cost} dataKey="tournament_edition_registered" template={(v) => {
                    return (`${i18nManager.getCurrencySign(v.registration_amount.currency_code)} ${StringHelper.toPresentableMoneyValue(v.registration_amount.amount)}`);
                }} />
                <Column className="table-c" doNotRender={width < 800 || showRecent || !!dataComponent} header={labels.table.headers.seed} dataKey="{tournament_edition_registered.seed_number}" template={(v) => {
                    return (`#${v}`);
                }} />
                <Column className="table-c" doNotRender={!!dataComponent} header={labels.table.headers.status} dataKey="{tournament_edition_registered.status}" template={(v) => buildStatusLabel(v)} />
                <Column className="table-c" doNotRender={width < 800 || !!dataComponent} template={(v) => (<i className="fa fa-angle-right" />)} />
            </Table>
            {(!showRecent && tournamentParticipants?.content) && (<div className="pagination">
                {Elements.buildPaginator(tournamentParticipants ?? {},
                    ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchInformation),
                    parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
                {(width > 800) && Elements.buildSorter(labels.table.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchInformation), labels.table.sorts[0].label)}
            </div>)}
        </div>
    </div>);

    function fetchInformation() {
        setTournamentParticipants({});
        tournamentService.queryTournamentEditionRegisteredParticipants(tournamentEdition.external_id, {
            sort: searchParams.get("sort") ?? "",
            type: searchParams.get("type") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
            "tournament_edition_registered.status": searchParams.get("status") ?? "",
        }).then(({ apiResponse }: SanitizedResponse<any>) => {
            setTournamentParticipants(apiResponse.data);
        }).catch(BaseService.reportError);
    }

    function buildStatusLabel(status: RegisteredTournamentStatus) {
        const color = {
            [RegisteredTournamentStatus.REMOVED]: "#0D0D0D",
            [RegisteredTournamentStatus.WITHDRAWN]: "#D55C5C",
            [RegisteredTournamentStatus.REGISTERED]: "#49C564",
        }[status];
        const label = labels.query_inputs.find((i) => i.name === "status")?.options?.find((o) => o.value === status)?.label ?? status;
        return (<span style={{ fontWeight: "bold", color }}>{label}</span>);
    }

    function buildRegisteredTournamentDetails(tournamentParticipant: ITournamentEditionRegisteredParticipant) {
        const name = Utils.getResourceName(tournamentParticipant.resource);
        const imageSrc = Utils.getResourceImage(tournamentParticipant.resource);
        const isWithDrawnOrRemoved = BoolHelper.equalsAny(tournamentParticipant.tournament_edition_registered?.status, [RegisteredTournamentStatus.WITHDRAWN, RegisteredTournamentStatus.REMOVED]);

        return (<div key={tournamentParticipant.external_id} className="mt-participant-data">
            <div className="mt-participant-data-hctrl">
                <Snapper header={{
                    [RegisteredTournamentStatus.REGISTERED]: labels.detail.participant_snap,
                    [RegisteredTournamentStatus.REMOVED]: labels.detail.removed_participant_snap,
                    [RegisteredTournamentStatus.WITHDRAWN]: labels.detail.withdrawn_participant_snap,
                }[tournamentParticipant.tournament_edition_registered.status]} template={[{ p: "participant_name", v: name }]} />
                <i className="fa fa-times close" onClick={() => setDataComponent?.(undefined)} />
            </div>
            <div className="mt-participant-data-details">
                <img alt={name} src={imageSrc} />
                <span className="n">{name}</span>
                <span className="noseur-pill tf-pill-upcoming" style={{ marginLeft: 6, padding: "0.2rem 0.6rem" }}>{tournamentParticipant.type === "TEAM" ? labels.detail.team : labels.detail.member}</span>
            </div>
            <div className="mt-participant-data-info">
                <div>
                    <span className="k">{labels.detail.seed}</span>
                    <span className="v">#{tournamentParticipant.tournament_edition_registered.seed_number}</span>
                </div>
                <div>
                    <span className="k">{labels.detail.status}</span>
                    <span className="v">{buildStatusLabel(tournamentParticipant.tournament_edition_registered.status)}</span>
                </div>
                <div>
                    <span className="k">{labels.detail.registration_cost}</span>
                    <span className="v">{(i18nManager.getCurrencySign(tournamentParticipant.tournament_edition_registered.registration_amount?.currency_code) ?? "") + " " + StringHelper.toPresentableMoneyValue(tournamentParticipant.tournament_edition_registered.registration_amount?.amount ?? 0)}</span>
                </div>
                {(BoolHelper.equalsAny(tournamentParticipant.tournament_edition_registered.status, [RegisteredTournamentStatus.WITHDRAWN, RegisteredTournamentStatus.REMOVED])) && (<div>
                    <span className="k">{labels.detail.refunded_amount}</span>
                    <span className="v">{(i18nManager.getCurrencySign(tournamentParticipant.tournament_edition_registered.refund_amount?.currency_code ?? "")) + " " + StringHelper.toPresentableMoneyValue(tournamentParticipant.tournament_edition_registered.refund_amount?.amount ?? 0)}</span>
                </div>)}
                <div>
                    <span className="k">{labels.detail.registration_date}</span>
                    <span className="v">{Utils.formatToFineDate(tournamentParticipant.tournament_edition_registered.registration_date ?? "", true)}</span>
                </div>
                {(BoolHelper.equalsAny(tournamentParticipant.tournament_edition_registered.status, [RegisteredTournamentStatus.WITHDRAWN, RegisteredTournamentStatus.REMOVED])) && (<div>
                    <span className="k">{tournamentParticipant.tournament_edition_registered.status === RegisteredTournamentStatus.REMOVED ? labels.detail.removal_date : labels.detail.withdrawal_date}</span>
                    <span className="v">{Utils.formatToFineDate(tournamentParticipant.tournament_edition_registered.withdrawal_or_removal_date ?? "", true)}</span>
                </div>)}
            </div>
            {(tournamentParticipant.tournament_edition_registered.status === RegisteredTournamentStatus.REGISTERED) && (<div className="mt-participant-data-actions">
                <span className="link danger" onClick={() => removeFromTournament(tournamentParticipant)}>{labels.detail.remove_from_tournament}</span>
                <span className="link">{labels.detail.send_private_message}</span>
                <span className="link" onClick={() => sendGift(tournamentParticipant)}>{labels.detail.send_gift}</span>
            </div>)}
            {(isWithDrawnOrRemoved) && (<div className="mt-participant-data-actions" style={{ display: "unset" }}>
                <span style={{ opacity: 0.5 }}>{tournamentParticipant.tournament_edition_registered?.status === RegisteredTournamentStatus.WITHDRAWN ? labels.detail.withdrawal_reason : labels.detail.removal_reason}</span>
                {" "}<span>{tournamentParticipant.tournament_edition_registered.withdrawal_or_removal_reason}</span>
            </div>)}
            <div className="mt-participant-data-cfs">
                <span className="t">{labels.detail.custom_fields_answers}</span>
                {Object.entries(tournamentParticipant.tournament_edition_registered.custom_fields_answers).map(([key, value]) => {
                    const label = tournamentParticipant.tournament_edition_registered.custom_fields_id_map?.[key]?.label ?? customFields?.[key] ?? key;

                    return (<div key={key}>
                        <span className="k">{label} - </span>
                        <span className="v">{value}</span>
                    </div>);
                })}
            </div>
            {(tournamentParticipant.type === RegisteredTournamentParticipantType.TEAM) && (<div className="mt-participant-data-tmems">
                <div className="h">
                    <Snapper disabled={isWithDrawnOrRemoved} header={labels.detail.participant_members_snap} template={[{ p: "participant_name", v: name }]} />
                </div>
                {tournamentParticipant.participants.map((tm) => (<div key={tm.external_id} className="mt-participant-data-tmem">
                    <Snapper disabled={isWithDrawnOrRemoved} header={labels.detail.participant_member_snap} template={[{ p: "participant_name", v: name }, { p: "mem_name", v: tm.display_name }]} />
                    <div className="profile-brief" style={{ marginLeft: 20, marginTop: 0 }}>
                        <img alt={tm.display_name} src={tm.passport_picture_url} />
                        <div>
                            <span className="u lined-limit">{tm.display_name}</span>
                            <span className="pd lined-limit">{tm.profile_details}</span>
                        </div>
                    </div>
                </div>))}
                {(tournamentParticipant.extra_participants?.length > 0) && (<>
                    <div style={{ padding: "15px 0px", marginBottom: 10, borderTop: "1px solid var(--underlineColor)", borderBottom: "1px solid var(--underlineColor)" }}>{labels.substitutes}</div>
                    {tournamentParticipant.extra_participants.map((tm) => (<div key={tm.external_id} className="mt-participant-data-tmem">
                        <Snapper disabled={isWithDrawnOrRemoved} header={labels.detail.participant_member_snap} template={[{ p: "participant_name", v: name }, { p: "mem_name", v: tm.display_name }]} />
                        <div className="profile-brief" style={{ marginLeft: 20, marginTop: 0 }}>
                            <img alt={tm.display_name} src={tm.passport_picture_url} />
                            <div>
                                <span className="u lined-limit">{tm.display_name}</span>
                                <span className="pd lined-limit">{tm.profile_details}</span>
                            </div>
                        </div>
                    </div>))}
                </>)}
            </div>)}
        </div>);
    }

    function seedParticipants() {
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.seed_participants;
        let dTournamentParticipants: ITournamentEditionRegisteredParticipant[] = []; // TODO FETCH ALL REGISTERED

        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.update_seeding,
            onLoading: async (alert: AlertInterface) => {
                setTimeout(() => {
                    dTournamentParticipants = TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS;
                    alert.doneLoading(() => {
                        alert.update({
                            __pass_through__: true,
                            style: { maxWidth: "90%" },
                            content: (<div style={{ marginTop: 20 }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <RadioButton name="seed-strategy" style={{ marginRight: 20 }} label={dialogLabels.seed_by_registration} />
                                    <RadioButton name="seed-strategy" style={{ marginRight: 20 }} label={dialogLabels.auto_seed_by_randomization} />
                                    <RadioButton name="seed-strategy" label={dialogLabels.manual_seeding} />
                                </div>
                                <Table stripedRows scheme={Schemes.OMNEHOST} data={dTournamentParticipants} totalRecords={dTournamentParticipants.length} dataRefreshKeys={["external_id", "created_at", "status"]}
                                    style={{ flex: 1, width: "100%", marginTop: 30 }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE} >
                                    <Column className="table-c" doNotRender={!!dataComponent} header={dialogLabels.table.headers.seed_number} dataKey="{tournament_edition_registered.seed_number}" template={(v) => {
                                        return (<TextInput defaultValue={v} scheme={Schemes.OMNEHOST} style={{ maxWidth: 100 }} onInputComplete={(v) => {
                                            dialogErrorRef.current!.innerText = " ";
                                        }} />);
                                    }} />
                                    <Column className="table-c" header={dialogLabels.table.headers.name} dataKey="resource" template={(v) => {
                                        const name = Utils.getResourceName(v);

                                        return (<div style={{ display: "flex", alignItems: "center" }}>
                                            <img alt={name} src={Utils.getResourceImage(v)} style={{ width: 20, height: 20, borderRadius: "100%", marginRight: 5 }} />
                                            <span>{name}</span>
                                        </div>)
                                    }} />
                                    <Column className="table-c" header={dialogLabels.table.headers.registration_date} dataKey="{tournament_edition_registered.registration_date}" template={(v) => Utils.formatToFineDate(v, true)} />
                                </Table>
                                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
                            </div>),
                        } as any);
                    });
                }, 1000);
                return false;
            },
            onConfirm: async (button?: ButtonManageRef) => {
                /*if (tournamentName !== registeredTournament.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }*/
                button?.setLoadingState(true);
                /*tournamentService.withdrawTournamentEditionRegistered(registeredTournament.external_id, reason).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchTournaments();
                        Siren.alert(dialogLabels.withdrawal_request_submitted);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });*/
                return false;
            }
        });
        dialog.show();
    }

    function importParticipants() {
        alert("PENDING IMPLEMENTATION")
    }

    function sendInvites() {
        const emailAddresses: string[] = [""];
        const dialogLabels = labels.dialog.invite_participants;

        const updateSelectedParticipants = () => {
            if (!activeDialogMutableRoot.current) return;
            const permissions = emailAddresses.map((address: string, index: number) => {
                return (<div key={DOMHelper.uniqueElementId("tour-invs")} style={{ display: "flex", alignItems: "center", marginBottom: 15 }} ref={(r: any) => r?.scrollIntoView()}>
                    <i className={Classname.build("fa fa-times noseur-danger-tx", { "noseur-disabled": emailAddresses.length === 1 })} style={{ cursor: "pointer" }} onClick={() => {
                        emailAddresses.splice(index, 1);
                        console.log("---", emailAddresses);
                        updateSelectedParticipants();
                    }} />
                    <FormControl isFieldset fill label={dialogLabels.email_address} style={{ marginLeft: 20 }}>
                        <TextInput defaultValue={address} scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                            emailAddresses[index] = v;
                        }} />
                    </FormControl>
                </div>);
            });
            activeDialogMutableRoot.current.render(permissions);
        }

        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.send_invite,
            content: (<div style={{ marginTop: 20, marginBottom: 20 }}>
                <div style={{ flex: 1 }} ref={(r: any) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(activeDialogMutableRoot, ReactDOM.createRoot(r));
                    updateSelectedParticipants();
                }}></div>
                <span className="link" onClick={() => {
                    emailAddresses.push("");
                    updateSelectedParticipants();
                }}>{dialogLabels.add_new_email_address}</span>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                /*if (tournamentName !== registeredTournament.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }*/
                button?.setLoadingState(true);
                /*tournamentService.withdrawTournamentEditionRegistered(registeredTournament.external_id, reason).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchTournaments();
                        Siren.alert(dialogLabels.withdrawal_request_submitted);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });*/
                return false;
            }
        });
        dialog.show();
    }

    function exportParticipants() {
        const dialogLabels = labels.dialog.export_participants;

        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.export,
            content: (<div style={{ marginTop: 20, marginBottom: 20 }}>
                <Checkbox scheme={Schemes.OMNEHOST} defaultChecked={true} style={{ marginBottom: 10 }} label={dialogLabels.registration_date} />
                <Checkbox scheme={Schemes.OMNEHOST} defaultChecked={true} style={{ marginBottom: 10 }} label={dialogLabels.name} />
                <Checkbox scheme={Schemes.OMNEHOST} defaultChecked={true} style={{ marginBottom: 10 }} label={dialogLabels.registration_amount} />
                <Checkbox scheme={Schemes.OMNEHOST} defaultChecked={true} style={{ marginBottom: 10 }} label={dialogLabels.refund_amount} />
                <Checkbox scheme={Schemes.OMNEHOST} defaultChecked={true} style={{ marginBottom: 10 }} label={dialogLabels.seed} />
                <Checkbox scheme={Schemes.OMNEHOST} defaultChecked={true} style={{ marginBottom: 10 }} label={dialogLabels.status} />
                <Checkbox scheme={Schemes.OMNEHOST} defaultChecked={true} style={{ marginBottom: 10 }} label={dialogLabels.custom_fields_answers} />
                <Checkbox scheme={Schemes.OMNEHOST} defaultChecked={true} style={{ marginBottom: 10 }} label={dialogLabels.includes_amount_currencies} />
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                /*if (tournamentName !== registeredTournament.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }*/
                button?.setLoadingState(true);
                /*tournamentService.withdrawTournamentEditionRegistered(registeredTournament.external_id, reason).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchTournaments();
                        Siren.alert(dialogLabels.withdrawal_request_submitted);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });*/
                return false;
            }
        });
        dialog.show();
    }

    function removeFromTournament(tournamentParticipant: ITournamentEditionRegisteredParticipant) {
        let reason = "", participantName = "";
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.remove_from_tournament;
        const name = Utils.getResourceName(tournamentParticipant.resource);

        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            content: (<div style={{ marginTop: 20 }}>
                <FormControl isFieldset fill label={dialogLabels.participant_name}>
                    <TextInput placeholder={name} scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        participantName = v;
                    }} />
                </FormControl>
                <FormControl isFieldset fill label={dialogLabels.reason} style={{ marginTop: 15 }}>
                    <TextAreaInput scheme={Schemes.OMNEHOST} style={{ height: 150, width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        reason = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (participantName !== name) {
                    dialogErrorRef.current!.innerText = labels.dialog.name_must_match.replace("${name}", name);
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.removeTournamentEditionRegistered(tournamentParticipant.tournament_edition_registered.external_id, reason).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation();
                        setDataComponent?.(undefined);
                        Siren.alert(dialogLabels.participant_removed_successfully);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function sendGift(tournamentParticipant: ITournamentEditionRegisteredParticipant) {
        let amount = 0;
        let currency = "NGN";
        let participantName = "";
        const dialogLabels = labels.dialog.send_gift;
        const dialogErrorRef = { current: null } as any;
        const name = Utils.getResourceName(tournamentParticipant.resource);

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.send,
            desc: dialogLabels.desc.replaceAll("${participant_name}", name),
            content: (<div style={{ marginTop: 20 }}>
                <Dropdown fill noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: dialogLabels.currency, }}
                    textInputProps={{ style: { paddingLeft: 15 } }} options={tournamentCurrencies.map((c) => ({ value: c, label: `${i18nManager.getCurrencyName(c.currency_code)} (${c.currency_code})` }))}
                    onSelectOption={({ value }) => {
                        console.log(value);
                        currency = value.currency_code;
                    }} />
                <FormControl isFieldset fill style={{ marginTop: 15 }} label={dialogLabels.participant_name}>
                    <TextInput placeholder={name} scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        participantName = v;
                    }} />
                </FormControl>
                <div style={{ marginTop: 15 }} ref={(r: any) => {
                    if (!r) return;
                }}></div>
                {/* <FormControl isFieldset fill label={dialogLabels.amount_max.replaceAll("${currency_sign}", i18nManager.getCurrencySign(currency)).replaceAll("${max_amount}", "1000")}>
                    <TextInput scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        amount = parseInt(v) * 1000000;
                    }} />
                </FormControl> */}
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (participantName !== name) {
                    dialogErrorRef.current!.innerText = labels.dialog.name_must_match.replace("${name}", name);
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.sendTournamentEditionRegisteredGift(tournamentParticipant.tournament_edition_registered.external_id, currency, amount).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        Siren.alert(dialogLabels.gift_sent_successfully.replaceAll("${name}", name).replaceAll("${currency_sign}", i18nManager.getCurrencySign(currency)).replaceAll("${max_amount}", "1000"));
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Participants;
